/** Admin — Places CRUD + workflow (municipality-managed). */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Send, Archive, Trash2, MapPin, Pencil } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/app/providers/AuthProvider';
import { listPlaces, createPlace, updatePlace, publishPlace, archivePlace, deletePlace } from '@/services/api/places';
import { formatApiError } from '@/services/api/client';
import { CoverImage } from '@/components/media/Cover';
import { PlaceFormDialog } from '@/pages/admin/forms/PlaceFormDialog';
import { ADMIN_MANAGE } from '@/constants/testIds';

const STATUS_LABELS = {
	approved: 'Approvato', pending: 'In attesa', rejected: 'Rifiutato',
	published: 'Pubblicato', unpublished: 'Non pubblicato', draft: 'Bozza', archived: 'Archiviato',
};
const statusLabel = (s) => STATUS_LABELS[s] || s;

export default function PlacesAdmin() {
	const { can } = useAuth();
	const qc = useQueryClient();
	const [dialog, setDialog] = useState({ open: false, initial: null });
	const { data = [] } = useQuery({ queryKey: ['places'], queryFn: listPlaces });
	const inv = () => qc.invalidateQueries({ queryKey: ['places'] });
	const ok = (msg) => () => { toast.success(msg); inv(); };
	const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
	const createM = useMutation({ mutationFn: createPlace, onSuccess: () => { toast.success('Luogo creato'); setDialog({ open: false, initial: null }); inv(); }, onError: err });
	const updateM = useMutation({ mutationFn: ({ id, body }) => updatePlace(id, body), onSuccess: () => { toast.success('Luogo aggiornato'); setDialog({ open: false, initial: null }); inv(); }, onError: err });
	const pubM = useMutation({ mutationFn: publishPlace, onSuccess: ok('Pubblicato'), onError: err });
	const arcM = useMutation({ mutationFn: archivePlace, onSuccess: ok('Archiviato'), onError: err });
	const delM = useMutation({ mutationFn: deletePlace, onSuccess: ok('Eliminato'), onError: err });

	const onSubmit = (body) => {
		if (dialog.initial) updateM.mutate({ id: dialog.initial.id, body });
		else createM.mutate(body);
	};

	return (
		<div data-testid={ADMIN_MANAGE.placesPage} className="mx-auto max-w-5xl">
			<div className="flex items-center justify-between">
				<h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Poppins', sans-serif" }}>Luoghi</h1>
				{can('manage_content') && (
					<Button data-testid={ADMIN_MANAGE.placeCreateBtn} onClick={() => setDialog({ open: true, initial: null })} className="bg-emerald-400 text-slate-950 hover:bg-emerald-300"><Plus size={16} className="mr-1" /> Nuovo luogo</Button>
				)}
			</div>

			<PlaceFormDialog
				open={dialog.open}
				onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
				initial={dialog.initial}
				onSubmit={onSubmit}
				pending={createM.isPending || updateM.isPending}
			/>

			<div className="mt-6 space-y-2">
				{data.map((p) => (
					<div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3">
						<div className="flex min-w-0 items-center gap-3">
							<CoverImage url={p.cover_url} icon={MapPin} className="h-12 w-12 shrink-0 rounded-lg" />
							<div><p className="font-semibold">{p.name}</p><p className="text-xs text-slate-500">{p.category} · {p.slug}</p></div>
						</div>
						<div className="flex items-center gap-2">
							<Badge className={p.publication_status === 'published' ? 'bg-sky-400/10 text-sky-300' : 'bg-slate-700/40 text-slate-400'}>{statusLabel(p.publication_status)}</Badge>
							{can('manage_content') && <Button size="sm" variant="outline" data-testid={ADMIN_MANAGE.placeEditBtn} onClick={() => setDialog({ open: true, initial: p })} className="border-slate-600 text-slate-300"><Pencil size={13} /></Button>}
							{can('publish_content') && p.publication_status !== 'published' && <Button size="sm" variant="outline" onClick={() => pubM.mutate(p.id)} className="border-sky-400/40 text-sky-300"><Send size={13} className="mr-1" />Pubblica</Button>}
							{can('manage_content') && p.status !== 'archived' && <Button size="sm" variant="outline" data-testid="place-archive-button" onClick={() => arcM.mutate(p.id)} className="border-slate-600 text-slate-300"><Archive size={13} /></Button>}
							{can('manage_content') && <Button size="sm" variant="outline" data-testid="place-delete-button" onClick={() => delM.mutate(p.id)} className="border-rose-400/40 text-rose-300"><Trash2 size={13} /></Button>}
						</div>
					</div>
				))}
				{data.length === 0 && <p className="text-slate-500">Nessun luogo.</p>}
			</div>
		</div>
	);
}
